import { useRef, useState } from 'react';
import { operatorDetails } from '../data/legalPages';
import { submitClubQuoteLead } from '../lib/commerceApi';
import { mailtoFallbackMessage } from '../lib/leadCaptureGate';
import { trackEvent } from '../lib/analytics';

type ClubQuoteIntakeProps = {
  source: string;
};

export function ClubQuoteIntake({ source }: ClubQuoteIntakeProps) {
  const [message, setMessage] = useState('Vul de aanvraag in. We gebruiken je gegevens alleen om het pakketadvies op te volgen.');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formStartTracked = useRef(false);

  const surfaceLabels: Record<string, string> = {
    indoor: 'Indoor / sporthal',
    outdoor: 'Outdoor / buitenbaan',
    mixed: 'Beide'
  };
  const budgetLabels: Record<string, string> = {
    starter: 'Starterpakket',
    balanced: 'Gebalanceerd clubpakket',
    premium: 'Premium pakketadvies'
  };

  function formValue(data: FormData, key: string): string {
    return String(data.get(key) ?? '').trim();
  }

  function trackFormStartOnce() {
    if (formStartTracked.current) return;
    formStartTracked.current = true;
    trackEvent({ event: 'form_start', location: source, label: 'club_quote', routeFamily: 'lead_capture' });
  }

  return (
    <section className="quoteIntake" aria-label="Club en school offerte-intake">
      <div>
        <p className="eyebrow">Club & schoolpakket</p>
        <h2>Vertel wat je nodig hebt voor les, clinic of vereniging.</h2>
        <p>
          Vul organisatie, spelersaantal en speellocatie in. We zetten je aanvraag direct klaar als e-mail, zodat we het club- of schoolpakket gericht kunnen opvolgen.
        </p>
        <ul className="leadTrust" aria-label="Wat je kunt verwachten">
          <li>Persoonlijk pakketadvies op maat, geen automatische offerte.</li>
          <li>Nederlandstalige service voor clubs en scholen in NL en Belgie.</li>
          <li>Je gegevens gebruiken we alleen om dit pakketadvies op te volgen.</li>
          <li>Dit is een adviesaanvraag, geen bestelling of betaalverplichting.</li>
        </ul>
      </div>
      <form
        className="quoteForm"
        noValidate={false}
        onFocus={trackFormStartOnce}
        onSubmit={async (event) => {
          event.preventDefault();
          const data = new FormData(event.currentTarget);
          const organization = formValue(data, 'organization');
          const email = formValue(data, 'email');
          const players = formValue(data, 'players');
          const surface = formValue(data, 'surface');
          const budget = formValue(data, 'budget');
          const consent = data.get('consent') === 'on';
          if (!consent) {
            setMessage('Bevestig dat we je mogen mailen over dit pakketadvies.');
            trackEvent({ event: 'form_blocked', location: source, label: 'missing_consent', routeFamily: 'lead_capture' });
            return;
          }
          setIsSubmitting(true);
          const body = [
            'Nieuwe club/schoolpakket aanvraag via ProPickleball.nl',
            '',
            `Organisatie: ${organization}`,
            `E-mailadres: ${email}`,
            `Aantal spelers: ${players}`,
            `Speellocatie: ${surfaceLabels[surface] ?? surface}`,
            `Budgetindicatie: ${budgetLabels[budget] ?? budget}`,
            `Bronpagina: ${source}`,
            '',
            'Let op: deze aanvraag is prelaunch en geen bestelling.'
          ].join('\n');
          trackEvent({ event: 'form_submit_attempt', location: source, label: 'club_quote' });
          try {
            await submitClubQuoteLead({
              organization,
              email,
              players: Number(players),
              surface: surface as 'indoor' | 'outdoor' | 'mixed',
              budget: budget as 'starter' | 'balanced' | 'premium',
              consent: true,
              source
            });
            trackEvent({ event: 'generate_lead', location: source, label: 'club_quote', routeFamily: 'lead_capture' });
            setMessage('Dank je. Je pakketaanvraag is ontvangen; we volgen deze op via e-mail.');
            event.currentTarget.reset();
          } catch (error) {
            window.location.href = `mailto:${operatorDetails.email}?subject=${encodeURIComponent('Clubpakket aanvraag ProPickleball.nl')}&body=${encodeURIComponent(body)}`;
            trackEvent({ event: 'cta_click', location: source, label: 'club_quote_mailto_fallback', routeFamily: 'lead_capture' });
            setMessage(mailtoFallbackMessage(error, operatorDetails.email));
          } finally {
            setIsSubmitting(false);
          }
        }}
      >
        <label>
          Organisatie
          <input name="organization" placeholder="Club, school of bedrijf" autoComplete="organization" required />
        </label>
        <label>
          E-mailadres
          <input
            name="email"
            type="email"
            placeholder="naam@example.com"
            autoComplete="email"
            inputMode="email"
            aria-describedby="clubquote-status"
            required
          />
        </label>
        <label>
          Aantal spelers
          <input name="players" type="number" min="4" inputMode="numeric" placeholder="Bijv. 20" required />
        </label>
        <label>
          Speellocatie
          <select name="surface" defaultValue="indoor">
            <option value="indoor">Indoor / sporthal</option>
            <option value="outdoor">Outdoor / buitenbaan</option>
            <option value="mixed">Beide</option>
          </select>
        </label>
        <label>
          Budgetindicatie
          <select name="budget" defaultValue="starter">
            <option value="starter">Starterpakket</option>
            <option value="balanced">Gebalanceerd clubpakket</option>
            <option value="premium">Premium pakketadvies</option>
          </select>
        </label>
        <label className="checkboxLine">
          <input name="consent" type="checkbox" required />
          Je mag mij mailen over dit club- of schoolpakket.
        </label>
        <button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Versturen...' : 'Pakketadvies aanvragen'}</button>
        <p className="formNote" id="clubquote-status" role="status" aria-live="polite">{message}</p>
      </form>
    </section>
  );
}
